/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LearnerShell from "./learnerShell";
import { globalAlert } from "../actions/commonActions";
import { useAuth } from "./learnerAuth";
import { passwordPolicyOk, PASSWORD_MIN, resolveLmsShortName } from "./_learnerConfig";
import {
  learnerMeApi,
  learnerUpdateMeApi,
  learnerChangePasswordApi,
  learnerRegionsApi,
} from "./_learnerActions";

export const dedupeLocationsByName = (items = [], preferredId = "") => {
  const unique = new Map();

  items.forEach((item) => {
    const id = String(item?._id || "");
    const normalizedName = String(item?.name || "").trim().toLowerCase();
    const key = normalizedName || id;
    if (!key) return;

    if (!unique.has(key) || (preferredId && id === String(preferredId))) {
      unique.set(key, item);
    }
  });

  return [...unique.values()];
};

export const resolveLearnerDistrictId = (who = {}) =>
  who?.districtRef?._id ||
  (typeof who?.districtRef === "string" ? who.districtRef : "") ||
  who?.district?._id ||
  who?.district ||
  "";

// Profile: view/update learner details + change password. serviceArea follows
// serviceAudience server-side, so it's never edited here.
const LearnerProfile = () => {
  const { t } = useTranslation();
  const { setLearner } = useAuth();

  const [me, setMe] = useState(null);
  const [form, setForm] = useState(null);
  const [regions, setRegions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwVisible, setPwVisible] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [pwErrors, setPwErrors] = useState({});
  const [showRules, setShowRules] = useState(false);
  const [forceShowRules, setForceShowRules] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const passwordRules = [
    {
      key: "min",
      label: t("learner.lms.setPassword.ruleMin", "At least 8 characters"),
      ok: pw.next.length >= PASSWORD_MIN,
    },
    {
      key: "upper",
      label: t("learner.lms.setPassword.ruleUpper", "One capital letter"),
      ok: /[A-Z]/.test(pw.next),
    },
    {
      key: "lower",
      label: t("learner.lms.setPassword.ruleLower", "One small letter"),
      ok: /[a-z]/.test(pw.next),
    },
    {
      key: "number",
      label: t("learner.lms.setPassword.ruleNumber", "One number"),
      ok: /[0-9]/.test(pw.next),
    },
    {
      key: "special",
      label: t("learner.lms.setPassword.ruleSpecial", "One special character"),
      ok: /[^A-Za-z0-9]/.test(pw.next),
    },
  ];

  const allRulesOk = passwordRules.every((r) => r.ok);
  const isPopoverShowing = (showRules || forceShowRules) && !allRulesOk && pw.next.length > 0;

  const profileToForm = (who = {}, regionList = regions) => {
    const districtId = resolveLearnerDistrictId(who);
    const explicitRegion =
      who?.region?._id || who?.region || who?.districtRef?.region?._id || who?.districtRef?.region || "";
    const inferredRegion =
      explicitRegion ||
      regionList.find((region) =>
        (region?.districts || []).some((district) => String(district?._id) === String(districtId))
      )?._id ||
      "";

    return {
      firstName: who?.firstName || "",
      lastName: who?.lastName || "",
      jobTitle: who?.jobTitle || "",
      learnerOrganisation: who?.learnerOrganisation || "",
      mobile: who?.mobile || who?.phoneNumber || "",
      region: inferredRegion,
      district: districtId,
      newsletterOptIn: !!who?.newsletterOptIn,
    };
  };

  useEffect(() => {
    (async () => {
      try {
        const [meRes, regionRes] = await Promise.all([
          learnerMeApi(),
          learnerRegionsApi({ lmsShortName: resolveLmsShortName() }),
        ]);
        let regionList = [];
        if (regionRes?.data?.status === 200) {
          const data = regionRes?.data?.data;
          regionList = Array.isArray(data) ? data : data?.regions || [];
          setRegions(regionList);
        }
        if (meRes?.data?.status === 200) {
          const who = meRes?.data?.data?.learner || meRes?.data?.data || {};
          setMe(who);
          setForm(profileToForm(who, regionList));
        }
      } catch (e) {
        /* guard handles auth */
      }
      setLoading(false);
    })();
  }, []);

  const visibleRegions = useMemo(
    () => dedupeLocationsByName(regions, form?.region),
    [regions, form?.region]
  );
  const selectedRegion = useMemo(
    () => visibleRegions.find((region) => String(region?._id) === String(form?.region || "")),
    [visibleRegions, form?.region]
  );
  const districts = useMemo(
    () => dedupeLocationsByName(selectedRegion?.districts || [], form?.district),
    [selectedRegion, form?.district]
  );
  const districtExists = districts.some((district) => String(district?._id) === String(form?.district || ""));

  const setField = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: v,
      ...(k === "region" ? { district: "" } : {}),
    }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        mobile: form?.mobile?.trim() || "",
        phoneNumber: form?.mobile?.trim() || "",
      };
      const res = await learnerUpdateMeApi(payload);
      const { status, data, message } = res?.data || {};
      if (status === 200) {
        const who = data?.learner || data || {};
        setMe(who);
        setForm(profileToForm(who));
        setLearner(who);
        globalAlert("success", message || t("learner.lms.profile.saved", "Profile updated."));
      } else {
        globalAlert("warning", message);
      }
    } catch (e) {
      globalAlert("error", e?.response?.data?.message);
    }
    setSaving(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!pw.current) errs.current = t("learner.lms.errors.currentPasswordRequired", "Current password is required.");
    
    if (!pw.next) {
      errs.next = t("learner.lms.errors.newPasswordRequired", "New password is required.");
    } else if (!passwordPolicyOk(pw.next)) {
      errs.next = t(
        "learner.lms.errors.passwordPolicy",
        "Password must include at least {{n}} characters, one capital letter, one small letter, one number and one special character.",
        { n: PASSWORD_MIN }
      );
    }
    
    if (!pw.confirm) {
      errs.confirm = t("learner.lms.errors.confirmPasswordRequired", "Confirm password is required.");
    } else if (pw.next !== pw.confirm) {
      errs.confirm = t("learner.lms.errors.passwordMismatch", "Passwords must match.");
    }

    setPwErrors(errs);
    if (Object.keys(errs).length) {
      if (errs.next && !allRulesOk && pw.next) setForceShowRules(true);
      return;
    }
    setPwSaving(true);
    try {
      const res = await learnerChangePasswordApi({
        currentPassword: pw.current,
        newPassword: pw.next,
      });
      const { status, message } = res?.data || {};
      if (status === 200) {
        setPw({ current: "", next: "", confirm: "" });
        setPwErrors({});
        globalAlert("success", message || t("learner.lms.profile.passwordChanged", "Password changed."));
      } else {
        setPwErrors({ form: message || t("common.somethingWentWrong") });
      }
    } catch (e) {
      globalAlert("error", e?.response?.data?.message);
    }
    setPwSaving(false);
  };

  const field = (id, label, value, onChange, opts = {}) => (
    <div className="lms-profile-field position-relative">
      <label htmlFor={id}>{label}</label>
      <div className={opts.passwordToggle ? "lms-profile-password-wrap" : undefined}>
        <input
          id={id}
          className={`form-control ${opts.error ? "is-invalid" : ""}`}
          value={value}
          type={opts.passwordToggle && opts.passwordVisible ? "text" : opts.type || "text"}
          disabled={opts.disabled}
          autoComplete={opts.autoComplete || "off"}
          onChange={(e) => {
            onChange?.(e.target.value);
            if (opts.clearError) opts.clearError();
          }}
          onFocus={opts.onFocus}
          onBlur={opts.onBlur}
        />
        {opts.passwordToggle && (
          <button
            type="button"
            className="lms-profile-password-toggle"
            aria-label={
              opts.passwordVisible
                ? t("learner.lms.hidePassword", "Hide password")
                : t("learner.lms.showPassword", "Show password")
            }
            aria-controls={id}
            aria-pressed={opts.passwordVisible}
            onClick={opts.onPasswordToggle}
          >
            <i
              className={`fas ${opts.passwordVisible ? "fa-eye" : "fa-eye-slash"}`}
              aria-hidden="true"
            ></i>
          </button>
        )}
      </div>
      {opts.popoverNode}
      {opts.error && !opts.hideError && (
        <div className="error-text font-12 text-start pl-2 mt-1" role="alert">
          {opts.error}
        </div>
      )}
    </div>
  );

  return (
    <LearnerShell title={t("learner.lms.nav.profile", "Profile")}>
      <h1 className="lms-portal-title">{t("learner.lms.nav.profile", "Profile")}</h1>
      {loading && (
        <div className="lms-portal-loading">
          <i className="fas fa-circle-notch fa-spin mr-2"></i>
          {t("common.loading", "Loading…")}
        </div>
      )}
      {!loading && form && (
        <div className="lms-profile-grid">
          <section className="lms-player-card">
            <h2 className="lms-portal-h2">
              {t("learner.lms.profile.details", "Your details")}
            </h2>
            {field("pf-email", t("learner.lms.fields.email", "Email address"), me?.email || "", null, { disabled: true })}
            <div className="lms-profile-2col">
              {field("pf-first", t("learner.lms.fields.firstName", "First name"), form.firstName, (v) => setField("firstName", v))}
              {field("pf-last", t("learner.lms.fields.lastName", "Last name"), form.lastName, (v) => setField("lastName", v))}
            </div>
            {field("pf-org", t("learner.lms.fields.learnerOrganisation", "Your organisation / school"), form.learnerOrganisation, (v) => setField("learnerOrganisation", v))}
            <div className="lms-profile-2col">
              {field("pf-job", t("learner.lms.fields.jobTitle", "Job title"), form.jobTitle, (v) => setField("jobTitle", v))}
              {field("pf-mobile", t("learner.lms.fields.mobile", "Mobile number"), form.mobile, (v) => setField("mobile", v), { type: "tel", autoComplete: "tel" })}
            </div>
            <div className="lms-profile-2col">
              <div className="lms-profile-field">
                <label htmlFor="pf-region">{t("learner.lms.fields.region", "Region")}</label>
                <select
                  id="pf-region"
                  className="form-control"
                  value={form.region}
                  onChange={(e) => setField("region", e.target.value)}
                >
                  <option value="">{t("learner.lms.fields.selectRegion", "Select region")}</option>
                  {visibleRegions.map((region) => (
                    <option key={region._id} value={region._id}>
                      {region.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="lms-profile-field">
                <label htmlFor="pf-district">{t("learner.lms.fields.district", "District")}</label>
                <select
                  id="pf-district"
                  className="form-control"
                  value={form.district}
                  disabled={!form.region}
                  onChange={(e) => setField("district", e.target.value)}
                >
                  <option value="">
                    {form.region
                      ? t("learner.lms.fields.selectDistrict", "Select district")
                      : t("learner.lms.fields.selectRegionFirst", "Select a region first")}
                  </option>
                  {form.district && !districtExists && (
                    <option value={form.district}>{form.district}</option>
                  )}
                  {districts.map((district) => (
                    <option key={district._id} value={district._id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <label className="lms-auth-check lms-profile-check">
              <input
                type="checkbox"
                checked={form.newsletterOptIn}
                onChange={(e) => setField("newsletterOptIn", e.target.checked)}
              />
              <span>{t("learner.lms.fields.newsletterOptIn", "Send me occasional updates and newsletters")}</span>
            </label>
            <button className="btn lms-btn-primary" onClick={save} disabled={saving}>
              {saving ? <i className="fas fa-circle-notch fa-spin"></i> : t("common.save", "Save")}
            </button>
          </section>

          <section className="lms-player-card">
            <h2 className="lms-portal-h2">
              {t("learner.lms.profile.changePassword", "Change password")}
            </h2>
            <form onSubmit={changePassword} noValidate>
              {field("pw-cur", t("learner.lms.fields.currentPassword", "Current password"), pw.current, (v) => setPw((p) => ({ ...p, current: v })), {
                type: "password",
                autoComplete: "current-password",
                passwordToggle: true,
                passwordVisible: pwVisible.current,
                onPasswordToggle: () => setPwVisible((visible) => ({ ...visible, current: !visible.current })),
                error: pwErrors.current,
                clearError: () => setPwErrors((p) => ({ ...p, current: "" })),
              })}
              {field("pw-new", t("learner.lms.fields.newPassword", "New password"), pw.next, (v) => {
                setPw((p) => ({ ...p, next: v }));
                setShowRules(true);
                setForceShowRules(false);
              }, {
                type: "password",
                autoComplete: "new-password",
                passwordToggle: true,
                passwordVisible: pwVisible.next,
                onPasswordToggle: () => setPwVisible((visible) => ({ ...visible, next: !visible.next })),
                error: pwErrors.next,
                clearError: () => setPwErrors((p) => ({ ...p, next: "" })),
                hideError: isPopoverShowing,
                onFocus: () => setShowRules(true),
                onBlur: () => setShowRules(false),
                popoverNode: isPopoverShowing && (
                  <div className="lms-password-popover" id="sp-policy">
                    <ul className="lms-password-rules">
                      {passwordRules.map((rule) => (
                        <li key={rule.key} className={`${rule.ok ? "active" : ""} ${forceShowRules && !rule.ok ? "text-danger" : ""}`}>
                          <i className={`fas ${rule.ok ? "fa-check-circle" : forceShowRules ? "fa-times-circle text-danger" : "fa-circle"}`}></i>
                          <span>{rule.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ),
              })}
              {field("pw-cnf", t("learner.lms.fields.confirmPassword", "Confirm password"), pw.confirm, (v) => setPw((p) => ({ ...p, confirm: v })), {
                type: "password",
                autoComplete: "new-password",
                passwordToggle: true,
                passwordVisible: pwVisible.confirm,
                onPasswordToggle: () => setPwVisible((visible) => ({ ...visible, confirm: !visible.confirm })),
                error: pwErrors.confirm,
                clearError: () => setPwErrors((p) => ({ ...p, confirm: "" })),
              })}
              {pwErrors.form && (
                <div className="lms-portal-error" role="alert">{pwErrors.form}</div>
              )}
              <button type="submit" className="btn lms-btn-primary" disabled={pwSaving}>
                {pwSaving ? (
                  <i className="fas fa-circle-notch fa-spin"></i>
                ) : (
                  t("learner.lms.profile.changePassword", "Change password")
                )}
              </button>
            </form>
          </section>
        </div>
      )}
    </LearnerShell>
  );
};

export default LearnerProfile;
